import React from 'react';
import { Switch } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { DefaultOption, FormInput } from 'src/components/molecules';
import { FormInputVariant, FormState } from 'src/types';
import { QuestionData } from '../../types';
import { DatabaseFieldInfoMessage } from './DatabaseFieldInfoMessage';
import {
  DatabaseFieldContainer,
  DatabaseFieldContent,
  DatabaseFieldIconWrapper,
  DatabaseFieldToggleWrapper,
  StyledErrorMessage,
} from './QuestionCard.styles';

interface Props {
  question: QuestionData;
  databaseFieldSubTypeOptions: DefaultOption[];
  databaseFieldError: string | null;
  onDatabaseFieldChange: (value: unknown) => void;
  onDatabaseMultiSelectToggle: () => void;
  requiredSuffix: string;
  'data-testid'?: string;
}

export const DatabaseFieldSection: React.FC<Props> = ({
  question,
  databaseFieldSubTypeOptions,
  databaseFieldError,
  onDatabaseFieldChange,
  onDatabaseMultiSelectToggle,
  requiredSuffix,
  ...props
}) => {
  const testId = props['data-testid'];

  return (
    <DatabaseFieldContainer data-testid={`${testId}-database-field-wrapper`}>
      <DatabaseFieldIconWrapper data-testid={`${testId}-database-field-icon`}>
        <StyledIcon className="uil uil-corner-down-right" />
      </DatabaseFieldIconWrapper>
      <DatabaseFieldContent data-testid={`${testId}-database-field-content`}>
        <div>
          <FormInput
            variant={FormInputVariant.DROPDOWN}
            state={databaseFieldError ? FormState.ERROR : FormState.DEFAULT}
            label="Field"
            isRequired
            requiredText={requiredSuffix}
            options={databaseFieldSubTypeOptions}
            value={
              question.databaseFieldOptions?.selectedFieldId
                ? databaseFieldSubTypeOptions.find(
                    (opt) =>
                      opt.value ===
                      question.databaseFieldOptions?.selectedFieldId,
                  )
                : null
            }
            onChange={onDatabaseFieldChange}
            placeholder="Select database field"
            data-testid={`${testId}-database-field-dropdown`}
            $removeMarginBottom
            aria-invalid={!!databaseFieldError}
            aria-describedby={
              databaseFieldError ? `${testId}-database-field-error` : undefined
            }
          />
          {databaseFieldError && (
            <StyledErrorMessage
              id={`${testId}-database-field-error`}
              role="alert"
              data-testid={`${testId}-database-field-error-message`}
            >
              {databaseFieldError}
            </StyledErrorMessage>
          )}
        </div>
        <DatabaseFieldToggleWrapper
          data-testid={`${testId}-database-multiselect-toggle-wrapper`}
        >
          <Switch
            checked={question.databaseFieldOptions?.allowMultiSelect || false}
            onClick={onDatabaseMultiSelectToggle}
            label="Multiple choice"
            labelPosition="right"
            data-testid={`${testId}-database-multiselect-switch`}
          />
        </DatabaseFieldToggleWrapper>
        <DatabaseFieldInfoMessage
          data-testid={`${testId}-database-field-info`}
        />
      </DatabaseFieldContent>
    </DatabaseFieldContainer>
  );
};
